import { useCallback, useEffect, useMemo } from 'react';
import { DbuiV1GetDataSourceSummaryResponse } from '@antimatterhq/asl-api-ts-v1';
import { useNavigate } from 'react-router-dom';
import { generateSafeTestId } from '@antimatterhq/ui/utils/generateSafeTestId';
import { useAtomValue } from 'jotai';
import { Text } from '@antimatterhq/ui/components/typography';
import { Button } from '@antimatterhq/ui/components/button';
import { Icon } from '@antimatterhq/ui/components/icon';
import { useGenericFuzzyFiltering } from '@antimatterhq/ui/hooks/fuzzyFiltering/useGenericFuzzyFiltering';
import { useAtomCallback } from 'jotai/utils';
import { GlobalError } from '@antimatterhq/ui/components/globalError';
import { isAxiosError } from 'axios';
import Spacer from '@antimatterhq/ui/components/spacer';
import DatasourceCard from '@/components/dataSources/dataSourceCard/DataSourceCard';
import { DeleteDataSourceDialog } from '@/components/dataSources/deleteDataSourceDialog/DeleteDataSourceDialog';
import { DatasourceUpgradeDialog } from '@/components/dataSources/dataSourceCardAndTable/datasourceUpgradeDialog/DatasourceUpgradeDialog';
import { useGetDatasourcesSummary } from '@/hooks/dbui/useGetDataSourcesSummary';
import {
  dataSourceFilterAtom,
  dataSourceViewModeAtom,
} from '@/atoms/dataSources/dataSourcesAtoms';
import { DataSourceViewModeToggle } from '@/components/dataSources/dataSourceViewModeToggle/DataSourceViewModeToggle';
import { DataSourceFilter } from '@/components/dataSources/dataSourceFilter/DataSourceFilter';
import { CurrentDataSourcesTable } from '@/components/dataSources/currentDataSourceTable/CurrentDataSourceTable';
import { DataSourceCardSkeleton } from '@/components/dataSources/dataSourceCard/dataSourceCardSkeleton/DataSourceCardSkeleton';

interface CurrentDataSourcesViewProps {
  dataSources: NonNullable<DbuiV1GetDataSourceSummaryResponse['dataSources']>;
  isLoading: boolean;
}

const CurrentDataSourcesCardView = ({
  dataSources,
  isLoading,
}: CurrentDataSourcesViewProps) => {
  const navigate = useNavigate();

  const {
    data: summary,
    isLoading: isLoadingSummary,
    isFetching: isFetchingSummary,
  } = useGetDatasourcesSummary({
    alerts: true,
    metrics: true,
  });

  const summaryWithAlertsAndMetrics = useMemo(() => {
    if (!summary?.dataSources || isLoadingSummary || isFetchingSummary)
      return dataSources;

    return dataSources.map((dataSource) => {
      const summaryData = summary.dataSources?.find(
        (ds) => ds.name === dataSource.name,
      );
      if (summaryData) {
        return {
          ...dataSource,
          alerts: summaryData.alerts || dataSource.alerts,
          metrics: summaryData.metrics || dataSource.metrics,
        };
      }

      return dataSource;
    });
  }, [dataSources, isLoadingSummary, isFetchingSummary, summary?.dataSources]);

  const isLoadingMetricsAndAlerts = useMemo(() => {
    return isLoadingSummary || isFetchingSummary;
  }, [isLoadingSummary, isFetchingSummary]);

  if (isLoading) {
    return (
      <div
        className="grid grid-cols-1 gap-4 sm:grid-cols-2 md:grid-cols-3"
        data-testid="current-datasources-loading"
      >
        {Array.from({ length: 9 }).map((_, index) => (
          <DataSourceCardSkeleton
            key={index}
            variant="default"
            data-testid={`datasource-card-skeleton-${index}`}
          />
        ))}
      </div>
    );
  }

  if (!dataSources?.length && !isLoading) {
    return (
      <div
        className="m-auto flex h-fit w-full flex-col"
        data-testid="current-datasources-section-empty"
      >
        <div className="flex flex-col items-center justify-center gap-2">
          <Icon
            icon="antimatterhq:custom-datasource"
            width={100}
            className="text-grey-400 dark:text-grey-400"
          />
          <Spacer size="base" />
          <div className="flex flex-col items-center gap-2">
            <Text size="lg" semiBold color="info" className="text-grey-400">
              Add a datasource to get started.
            </Text>
            <Spacer size="base" />
            <Button
              variant="primary"
              onClick={() => navigate('/configure/data-sources/add')}
              data-testid="add-datasource-button"
            >
              <Icon icon="lucide:plus" />
              Add datasource
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <>
      <div
        className="m-auto flex h-fit w-full flex-col"
        data-testid="current-datasources-section"
      >
        <div
          className="grid grid-cols-1 gap-4 sm:grid-cols-2 md:grid-cols-3"
          data-testid="current-datasources-grid"
        >
          {summaryWithAlertsAndMetrics?.map((dataSource) => (
            <DatasourceCard
              key={dataSource.name}
              datasourceSummary={dataSource}
              isLoadingMetricsAndAlerts={isLoadingMetricsAndAlerts}
              data-testid={`datasource-card-${generateSafeTestId(
                dataSource.name,
              )}`}
            />
          ))}
        </div>
      </div>
    </>
  );
};

const CurrentDataSourcesTableView = ({
  dataSources,
  isLoading,
}: CurrentDataSourcesViewProps) => {
  return (
    <CurrentDataSourcesTable dataSources={dataSources} isLoading={isLoading} />
  );
};

const CurrentDataSourcesHeader = () => {
  const navigate = useNavigate();

  return (
    <div className="mb-2 flex flex-col gap-2">
      <div className="flex items-center gap-2">
        <Text
          size="lg"
          className="mb-4 mr-auto w-full text-xl font-bold"
          data-testid="current-datasources-title"
        >
          Current datasources
        </Text>
        <Button
          variant="primary"
          onClick={() => navigate('/configure/data-sources/add')}
          data-testid="add-datasource-button"
        >
          <Icon icon="lucide:plus" />
          Add datasource
        </Button>
      </div>
      <div className="flex items-center justify-between gap-2">
        <DataSourceFilter />
        <DataSourceViewModeToggle />
      </div>
    </div>
  );
};

const CurrentDataSourcesContent = () => {
  const viewMode = useAtomValue(dataSourceViewModeAtom);
  const fuzzyFilter = useAtomValue(dataSourceFilterAtom);

  const {
    data: summary,
    isLoading,
    isFetching,
    error,
    refetch,
  } = useGetDatasourcesSummary();

  const filteredDataSources = useGenericFuzzyFiltering(
    summary?.dataSources ?? [],
    fuzzyFilter ?? '',
    [
      { name: 'name', getFn: (item) => item.name },
      { name: 'description', getFn: (item) => item.description },
      { name: 'source', getFn: (item) => item.source },
      { name: 'sourceType', getFn: (item) => item.sourceType },
    ],
  );

  if (error) {
    const errorMessage = isAxiosError(error)
      ? error.response?.data.message
      : (error.message ?? 'Please try again later.');

    return (
      <GlobalError
        iconSize="sm"
        title="Unable to load datasources"
        description={errorMessage}
        onRetry={async () => {
          await refetch();
        }}
      />
    );
  }

  return (
    <>
      {viewMode === 'card' ? (
        <CurrentDataSourcesCardView
          dataSources={filteredDataSources}
          isLoading={isLoading || isFetching}
        />
      ) : (
        <CurrentDataSourcesTableView
          dataSources={filteredDataSources}
          isLoading={isLoading || isFetching}
        />
      )}
    </>
  );
};

export const CurrentDataSources = () => {
  const resetFuzzyFilter = useAtomCallback(
    useCallback((_get, set) => {
      set(dataSourceFilterAtom, '');
    }, []),
  );

  useEffect(() => {
    resetFuzzyFilter();
  }, [resetFuzzyFilter]);

  return (
    <>
      <CurrentDataSourcesHeader />
      <CurrentDataSourcesContent />
      <DeleteDataSourceDialog data-testid="delete-datasource-dialog" />
      <DatasourceUpgradeDialog />
    </>
  );
};
